import { useEffect, useRef, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import type { Camera, Site } from '../../types'

interface Props {
  site: Site
  cameras: Camera[]
  canManage: boolean
  onCameraLocationChanged?: (cameraId: string, lat: number, lng: number) => void
  onCameraLocationRemoved?: (cameraId: string) => void
  onSaveDefaultLocation?: (lat: number, lng: number) => void
}

// SVG camera icon as a data URI
const CAMERA_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width="16" height="16"><path d="M12 15.2a3.2 3.2 0 100-6.4 3.2 3.2 0 000 6.4z"/><path d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"/></svg>`

function cameraIcon(camNumber: number, selected: boolean) {
  const bg = selected ? '#0066FF' : '#D0021B'
  const border = selected ? '3px solid #fff' : '2.5px solid #fff'
  return L.divIcon({
    className: '',
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -20],
    html: `<div style="
      width:36px;height:36px;border-radius:50%;
      background:${bg};border:${border};
      display:flex;align-items:center;justify-content:center;
      box-shadow:0 2px 8px rgba(0,0,0,0.4);
      font-family:system-ui,sans-serif;
      position:relative;
    ">
      <img src="data:image/svg+xml,${encodeURIComponent(CAMERA_SVG)}" width="18" height="18" style="display:block"/>
      <span style="
        position:absolute;top:-6px;right:-6px;
        background:#1a1a1a;color:#fff;
        font-size:9px;font-weight:700;
        width:16px;height:16px;border-radius:50%;
        display:flex;align-items:center;justify-content:center;
        border:1.5px solid #fff;
      ">${camNumber}</span>
    </div>`,
  })
}

interface SearchResult {
  display_name: string
  lat: string
  lon: string
}

function bestFitGrid(count: number): number {
  if (count <= 1) return 1
  if (count <= 4) return 2
  if (count <= 9) return 3
  return 4
}

export function SiteMap({ site, cameras, canManage, onCameraLocationChanged, onCameraLocationRemoved, onSaveDefaultLocation }: Props) {
  const navigate = useNavigate()
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<L.Map | null>(null)
  const markersRef = useRef<Map<string, L.Marker>>(new Map())
  const [placingCamera, setPlacingCamera] = useState<Camera | null>(null)
  const placingRef = useRef<Camera | null>(null)

  // Multi-select state
  const [selectedCameras, setSelectedCameras] = useState<Set<string>>(new Set())
  const selectedRef = useRef<Set<string>>(selectedCameras)
  useEffect(() => { selectedRef.current = selectedCameras }, [selectedCameras])

  // Search state
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)

  // Manual coords state
  const [manualLat, setManualLat] = useState('')
  const [manualLng, setManualLng] = useState('')

  // Keep ref in sync for map click handler
  useEffect(() => { placingRef.current = placingCamera }, [placingCamera])

  const toggleCameraSelection = useCallback((cameraId: string) => {
    setSelectedCameras(prev => {
      const next = new Set(prev)
      if (next.has(cameraId)) {
        next.delete(cameraId)
      } else {
        next.add(cameraId)
      }
      return next
    })
  }, [])

  const openInViewer = useCallback((cameraIds: string[]) => {
    if (cameraIds.length === 0) return
    const gridSize = bestFitGrid(cameraIds.length)
    const slots: (string | null)[] = Array(16).fill(null)
    cameraIds.forEach((id, i) => { if (i < 16) slots[i] = id })
    const storageKey = `rv-viewer-${site.id}`
    localStorage.setItem(storageKey, JSON.stringify({ slots, gridSize }))
    navigate(`/sites/${site.id}/viewer`)
  }, [site.id, navigate])

  // Initialize map once
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return

    const camerasWithLocation = cameras.filter(c => c.latitude != null && c.longitude != null)
    let center: [number, number]
    let zoom: number

    if (site.latitude != null && site.longitude != null) {
      center = [site.latitude, site.longitude]
      zoom = 18
    } else if (camerasWithLocation.length > 0) {
      center = [camerasWithLocation[0].latitude!, camerasWithLocation[0].longitude!]
      zoom = 18
    } else {
      center = [51.5, -0.12]
      zoom = 6
    }

    const map = L.map(mapRef.current, {
      center,
      zoom,
      zoomControl: true,
    })

    // Satellite tile layer (Esri — free, no key)
    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
      attribution: 'Tiles &copy; Esri',
      maxZoom: 19,
    }).addTo(map)

    // Labels overlay on top of satellite
    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}', {
      maxZoom: 19,
    }).addTo(map)

    // Click handler for placing cameras
    map.on('click', (e: L.LeafletMouseEvent) => {
      const cam = placingRef.current
      if (cam && onCameraLocationChanged) {
        onCameraLocationChanged(cam.id, e.latlng.lat, e.latlng.lng)
        setPlacingCamera(null)
      }
    })

    mapInstanceRef.current = map

    return () => {
      map.remove()
      mapInstanceRef.current = null
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Update markers when cameras or selection change
  useEffect(() => {
    const map = mapInstanceRef.current
    if (!map) return

    // Clear old markers
    markersRef.current.forEach(m => m.remove())
    markersRef.current.clear()

    const locatedCameras = cameras.filter(c => c.latitude != null && c.longitude != null)

    locatedCameras.forEach((camera) => {
      const isSelected = selectedCameras.has(camera.id)
      const marker = L.marker(
        [camera.latitude!, camera.longitude!],
        { icon: cameraIcon(camera.cam_number, isSelected), draggable: canManage },
      ).addTo(map)

      // Tooltip on hover with camera details
      marker.bindTooltip(
        `<div style="font-family:system-ui,sans-serif;min-width:140px">
          <div style="font-weight:600;font-size:12px;color:#1a1a1a">${camera.name}</div>
          <div style="font-size:10px;color:#666;margin-top:2px">${camera.cam_ip}:${camera.port}</div>
          <div style="font-size:10px;color:#999;font-family:monospace">${camera.stream_name}</div>
          <div style="font-size:10px;color:#0066FF;margin-top:4px">Click to select · Double-click to view${canManage ? ' · Right-click to remove' : ''}</div>
        </div>`,
        { direction: 'top', offset: [0, -20], opacity: 0.95 },
      )

      // Right-click: popup with remove option
      if (canManage && onCameraLocationRemoved) {
        marker.bindPopup(
          `<div style="font-family:system-ui,sans-serif;text-align:center;min-width:120px">
            <div style="font-weight:600;font-size:12px;color:#1a1a1a;margin-bottom:8px">${camera.name}</div>
            <button id="rv-remove-${camera.id}" style="
              background:#dc2626;color:white;border:none;border-radius:6px;
              padding:5px 14px;font-size:11px;font-weight:600;cursor:pointer;
              width:100%;
            ">Remove from Map</button>
          </div>`,
          { closeButton: true, className: 'rv-marker-popup' },
        )
        marker.on('popupopen', () => {
          const btn = document.getElementById(`rv-remove-${camera.id}`)
          if (btn) {
            btn.onclick = () => {
              onCameraLocationRemoved(camera.id)
              marker.closePopup()
            }
          }
        })
        marker.on('contextmenu', (e) => {
          L.DomEvent.stopPropagation(e as unknown as Event)
          L.DomEvent.preventDefault(e as unknown as Event)
          marker.openPopup()
        })
      }

      // Single click: toggle selection
      marker.on('click', (e: L.LeafletMouseEvent) => {
        if (placingRef.current) return
        L.DomEvent.stopPropagation(e)
        toggleCameraSelection(camera.id)
      })

      // Double click: open single camera in viewer
      marker.on('dblclick', (e: L.LeafletMouseEvent) => {
        L.DomEvent.stopPropagation(e)
        openInViewer([camera.id])
      })

      // Allow dragging markers to reposition
      if (canManage && onCameraLocationChanged) {
        marker.on('dragend', () => {
          const pos = marker.getLatLng()
          onCameraLocationChanged(camera.id, pos.lat, pos.lng)
        })
      }

      markersRef.current.set(camera.id, marker)
    })
  }, [cameras, selectedCameras, canManage, onCameraLocationChanged, toggleCameraSelection, openInViewer])

  // Fit bounds on first camera load
  useEffect(() => {
    const map = mapInstanceRef.current
    if (!map) return
    const locatedCameras = cameras.filter(c => c.latitude != null && c.longitude != null)
    if (locatedCameras.length > 1 && site.latitude == null) {
      const markers = Array.from(markersRef.current.values())
      if (markers.length > 1) {
        const group = L.featureGroup(markers)
        map.fitBounds(group.getBounds().pad(0.1))
      }
    }
  }, [cameras.length]) // eslint-disable-line react-hooks/exhaustive-deps

  // Change cursor when placing
  useEffect(() => {
    const container = mapRef.current
    if (!container) return
    container.style.cursor = placingCamera ? 'crosshair' : ''
  }, [placingCamera])

  function handleSaveLocation() {
    const map = mapInstanceRef.current
    if (!map || !onSaveDefaultLocation) return
    const center = map.getCenter()
    onSaveDefaultLocation(center.lat, center.lng)
  }

  // Search using Nominatim (free OpenStreetMap geocoding)
  async function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    if (!searchQuery.trim()) return
    setSearching(true)
    setSearchResults([])
    setSearchError(null)
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery.trim())}&limit=5`
      const res = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'User-Agent': 'RedvisionCloudVMS/1.0',
        },
      })
      if (!res.ok) {
        setSearchError('Search failed')
        return
      }
      const data = await res.json() as SearchResult[]
      if (data.length === 0) {
        setSearchError('No results found')
      } else if (data.length === 1) {
        flyTo(parseFloat(data[0].lat), parseFloat(data[0].lon))
      } else {
        setSearchResults(data)
      }
    } catch {
      setSearchError('Search failed — check your connection')
    } finally {
      setSearching(false)
    }
  }

  function flyTo(lat: number, lng: number, zoom = 18) {
    mapInstanceRef.current?.flyTo([lat, lng], zoom, { duration: 1 })
    setSearchResults([])
    setSearchQuery('')
    setSearchError(null)
  }

  function handleManualPlace() {
    const lat = parseFloat(manualLat)
    const lng = parseFloat(manualLng)
    if (isNaN(lat) || isNaN(lng)) return
    if (placingCamera && onCameraLocationChanged) {
      onCameraLocationChanged(placingCamera.id, lat, lng)
      setPlacingCamera(null)
      setManualLat('')
      setManualLng('')
      flyTo(lat, lng)
    }
  }

  // Close search results when clicking outside
  useEffect(() => {
    if (searchResults.length === 0) return
    function handleClick() { setSearchResults([]) }
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [searchResults])

  const camerasWithoutLocation = cameras.filter(c => c.latitude == null || c.longitude == null)

  const inputCls = 'bg-gray-50 border border-gray-200 rounded-md px-3 py-1.5 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/20'

  return (
    <div className="space-y-3">
      {/* Search bar + actions */}
      <div className="relative" style={{ zIndex: 1000 }}>
        <div className="flex gap-2">
          <form onSubmit={handleSearch} className="flex gap-2 flex-1" onClick={(e) => e.stopPropagation()}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setSearchError(null) }}
              placeholder="Search location... (e.g. address, postcode, town)"
              className={`flex-1 ${inputCls}`}
            />
            <button
              type="submit"
              disabled={searching}
              className="bg-brand hover:bg-brand-dark text-white text-sm font-medium px-4 py-1.5 rounded-md transition-colors disabled:opacity-50"
            >
              {searching ? '...' : 'Search'}
            </button>
          </form>
          {canManage && onSaveDefaultLocation && (
            <button
              onClick={handleSaveLocation}
              className="border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm px-3 py-1.5 rounded-md transition-colors whitespace-nowrap"
              title="Save current map view as the default for this site"
            >
              Save View
            </button>
          )}
        </div>

        {/* Search error */}
        {searchError && (
          <div className="absolute top-full mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm text-gray-500">
            {searchError}
          </div>
        )}

        {/* Search results dropdown */}
        {searchResults.length > 0 && (
          <div
            className="absolute top-full mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {searchResults.map((r, i) => (
              <button
                key={i}
                onClick={() => flyTo(parseFloat(r.lat), parseFloat(r.lon))}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 border-b border-gray-100 last:border-0 transition-colors"
              >
                {r.display_name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Selected cameras bar */}
      {selectedCameras.size > 0 && (
        <div className="flex items-center gap-3 bg-brand/10 border border-brand/30 rounded-lg px-4 py-2.5">
          <span className="text-sm text-brand-dark font-medium">
            {selectedCameras.size} camera{selectedCameras.size !== 1 ? 's' : ''} selected
          </span>
          <button
            onClick={() => openInViewer(Array.from(selectedCameras))}
            className="bg-brand hover:bg-brand-dark text-white text-sm font-medium px-4 py-1.5 rounded-md transition-colors"
          >
            Open in Viewer
          </button>
          <button
            onClick={() => setSelectedCameras(new Set())}
            className="text-xs text-gray-500 hover:text-gray-900 transition-colors"
          >
            Clear
          </button>
        </div>
      )}

      {/* Map container */}
      <div
        ref={mapRef}
        className="w-full rounded-xl overflow-hidden border border-gray-200 shadow-sm"
        style={{ height: '500px' }}
      />

      {/* Placing mode banner */}
      {placingCamera && (
        <div className="bg-brand/10 border border-brand/30 rounded-lg px-4 py-3 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-brand-dark">
              Click on the map to place <strong>{placingCamera.name}</strong>, or enter coordinates:
            </p>
            <button
              onClick={() => setPlacingCamera(null)}
              className="text-xs text-gray-500 hover:text-gray-900 transition-colors"
            >
              Cancel
            </button>
          </div>
          <div className="flex gap-2 items-end">
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-gray-500 mb-1">Latitude</label>
              <input
                type="text"
                value={manualLat}
                onChange={(e) => setManualLat(e.target.value)}
                placeholder="e.g. 51.5074"
                className={`w-36 ${inputCls}`}
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-gray-500 mb-1">Longitude</label>
              <input
                type="text"
                value={manualLng}
                onChange={(e) => setManualLng(e.target.value)}
                placeholder="e.g. -0.1278"
                className={`w-36 ${inputCls}`}
              />
            </div>
            <button
              onClick={handleManualPlace}
              className="bg-brand hover:bg-brand-dark text-white text-xs font-medium px-3 py-1.5 rounded-md transition-colors"
            >
              Place
            </button>
          </div>
        </div>
      )}

      {/* Cameras without location */}
      {canManage && camerasWithoutLocation.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">
            Cameras without location ({camerasWithoutLocation.length})
          </h3>
          <p className="text-xs text-gray-400 mb-3">
            Select a camera, then click on the map or enter coordinates to place it. Drag existing markers to reposition.
          </p>
          <div className="flex flex-wrap gap-2">
            {camerasWithoutLocation.map((cam) => (
              <button
                key={cam.id}
                onClick={() => setPlacingCamera(cam)}
                className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
                  placingCamera?.id === cam.id
                    ? 'bg-brand text-white border-brand'
                    : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-brand hover:text-brand'
                }`}
              >
                {cam.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
